<script setup lang="ts">
import { computed, ref } from "vue";
import { useTranslation } from "i18next-vue";
import CardField from "../cards/CardField.vue";
import CameraOverlay from "@/components/field-observations/CameraOverlay.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { usePwaController } from "@/composable/usePWA";
import type { ExistingMedia } from "@/composable/useFieldObservationForm";
import type { MarkerSlot } from "@/composable/usePhotoMarkerMatching";

/**
 * Photos d'une observation, dans sa carte : vignettes (suppression, choix du
 * marqueur quand l'observation en a plusieurs), une case d'attente par marqueur
 * du plan encore sans photo, et la tuile « + ».
 * Ajout par la caméra sur tablette, le sélecteur de fichiers sur desktop, ou
 * un glisser-déposer d'images sur une case.
 */

const props = defineProps<{
  photos: ExistingMedia[];
  isUploading: boolean;
  /** Marqueurs proposés au choix sur une photo (vide : rien à départager). */
  choosableMarkers: MarkerSlot[];
  /** Marqueurs illustrés par chaque photo (par id d'image). */
  markersByPhoto: Map<number, string[]>;
  /** Marqueurs encore sans photo — une case d'attente chacun. */
  uncoveredMarkers: MarkerSlot[];
}>();

const emit = defineEmits<{
  /** Photos à envoyer, associées d'office au marqueur `markerHandle` s'il est donné. */
  (e: "upload", files: File[], markerHandle: string | null): void;
  (e: "delete", photoId: number): void;
  /** Ajoute ou retire ce marqueur pour la photo ; `null` les retire tous. */
  (e: "assign", photoId: number, markerHandle: string | null): void;
}>();

const { t } = useTranslation();
const { isMobileOrTablet } = usePwaController();

// `pendingMarker` : ajout déclenché depuis un marqueur non couvert — la photo
// envoyée lui est directement associée.
const photoInput = ref<HTMLInputElement | null>(null);
const showCameraOverlay = ref(false);
const pendingMarker = ref<string | null>(null);

const openPhotoPicker = (markerHandle: string | null = null): void => {
  pendingMarker.value = markerHandle;
  if (isMobileOrTablet()) showCameraOverlay.value = true;
  else photoInput.value?.click();
};

/** Consomme le marqueur armé à l'ouverture du sélecteur / de la caméra. */
const consumePendingMarker = (): string | null => {
  const handle = pendingMarker.value;
  pendingMarker.value = null;
  return handle;
};

const handleCameraCaptured = (files: File[]): void => {
  showCameraOverlay.value = false;
  emit("upload", files, consumePendingMarker());
};

const handlePhotoChange = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  const files = input.files?.length ? Array.from(input.files) : [];
  input.value = "";
  if (files.length) emit("upload", files, consumePendingMarker());
};

// ── Glisser-déposer d'images (desktop) ──────────────────────────────────────
// Cibles : la tuile « + » et les cases d'attente d'un marqueur (photo
// directement associée à ce marqueur).

/** Cible survolée : handle du marqueur, "new" pour la tuile « + », ou null. */
const dragOverTarget = ref<string | null>(null);

const onDragOver = (target: string): void => {
  dragOverTarget.value = target;
};

const onDragLeave = (event: DragEvent, target: string): void => {
  // Passer sur un enfant déclenche aussi `dragleave` : on ne réinitialise que
  // si le curseur quitte réellement la tuile.
  const related = event.relatedTarget as Node | null;
  const current = event.currentTarget as HTMLElement;
  if (related && current.contains(related)) return;
  if (dragOverTarget.value === target) dragOverTarget.value = null;
};

const onDropPhotos = (event: DragEvent, markerHandle: string | null): void => {
  dragOverTarget.value = null;
  const files = Array.from(event.dataTransfer?.files ?? []).filter((file) =>
    file.type.startsWith("image/"),
  );
  if (files.length) emit("upload", files, markerHandle);
};

/** Libellé d'un marqueur : son numéro, ou son horodatage tel quel. */
const slotLabel = (marker: MarkerSlot): string =>
  marker.number !== null ? `n°${marker.number}` : marker.label;

/** Libellés au choix : deux marqueurs de même numéro restent distinguables. */
const choiceLabels = computed(() => {
  const counts = new Map<string, number>();
  for (const marker of props.choosableMarkers) {
    const label = slotLabel(marker);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  return new Map(
    props.choosableMarkers.map((marker) => {
      const label = slotLabel(marker);
      const rank = (seen.get(label) ?? 0) + 1;
      seen.set(label, rank);
      return [
        marker.handle,
        (counts.get(label) ?? 0) > 1 ? `${label} (${rank})` : label,
      ];
    }),
  );
});

const photoBadge = (photoId: number): string => {
  const labels = (props.markersByPhoto.get(photoId) ?? []).map((handle) =>
    choiceLabels.value.get(handle),
  );
  return labels.length ? labels.join(", ") : "n° ?";
};
</script>

<template>
  <CardField :label="t('dashboard:fieldObservations.sections.photos')">
    <template #aside>
      <span v-if="isUploading" class="spinner-border spinner-border-sm" />
    </template>

    <div class="media-grid">
      <div v-for="photo in photos" :key="photo.id" class="media-item">
        <div v-if="photo.isLoading" class="media-loading">
          <span class="spinner-border spinner-border-sm" />
        </div>
        <img v-else :src="photo.url" alt="Photo" />
        <button
          type="button"
          class="btn-remove-media"
          @click="emit('delete', photo.id)"
        >
          <PhosphorIcon name="trash" :size="12" weight="bold" />
        </button>

        <!-- Plusieurs marqueurs : choix de ceux que la photo illustre -->
        <el-dropdown
          v-if="choosableMarkers.length"
          trigger="click"
          class="photo-number-dropdown"
          @click.stop
        >
          <button type="button" class="photo-number-badge" @click.stop>
            {{ photoBadge(photo.id) }}
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="marker in choosableMarkers"
                :key="marker.handle"
                @click="emit('assign', photo.id, marker.handle)"
              >
                <span class="d-inline-flex align-items-center gap-2">
                  {{ choiceLabels.get(marker.handle) }}
                  <PhosphorIcon
                    v-if="markersByPhoto.get(photo.id)?.includes(marker.handle)"
                    name="check"
                    :size="13"
                    weight="bold"
                  />
                </span>
              </el-dropdown-item>
              <el-dropdown-item divided @click="emit('assign', photo.id, null)">
                {{ t("canvas:observations.photos.noMarker") }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>

      <!-- Cases d'attente : marqueurs qui n'ont pas encore leur photo
           (clic ou glisser-déposer d'une image) -->
      <button
        v-for="marker in uncoveredMarkers"
        :key="`placeholder-${marker.handle}`"
        type="button"
        class="media-placeholder"
        :class="{ 'drag-over': dragOverTarget === marker.handle }"
        :title="
          t('canvas:observations.photos.addForMarker', {
            label: slotLabel(marker),
          })
        "
        @click="openPhotoPicker(marker.handle)"
        @dragover.prevent="onDragOver(marker.handle)"
        @dragenter.prevent
        @dragleave="onDragLeave($event, marker.handle)"
        @drop.prevent="onDropPhotos($event, marker.handle)"
      >
        <PhosphorIcon name="camera-plus" :size="18" />
        <span>{{ slotLabel(marker) }}</span>
      </button>

      <button
        type="button"
        class="add-media-btn"
        :class="{ 'drag-over': dragOverTarget === 'new' }"
        @click="openPhotoPicker(null)"
        @dragover.prevent="onDragOver('new')"
        @dragenter.prevent
        @dragleave="onDragLeave($event, 'new')"
        @drop.prevent="onDropPhotos($event, null)"
      >
        <PhosphorIcon name="plus" :size="18" />
      </button>
    </div>

    <input
      ref="photoInput"
      type="file"
      accept="image/*"
      multiple
      class="d-none"
      @change="handlePhotoChange"
    />

    <CameraOverlay
      v-if="showCameraOverlay"
      @close="showCameraOverlay = false"
      @captured="handleCameraCaptured"
    />
  </CardField>
</template>

<style lang="scss" scoped>
// Vignettes de taille bornée : le volet se redimensionne, et trois colonnes
// fixes donnaient des photos immenses une fois élargi. Ici la grille en met
// autant par ligne qu'il en tient (3 à la largeur par défaut du volet).
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 4px;
}

.media-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: var(--bs-border-radius);
  overflow: hidden;
  background-color: var(--bs-tertiary-bg);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.media-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remove-media {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.photo-number-dropdown {
  position: absolute;
  bottom: 2px;
  left: 2px;
}

.photo-number-badge {
  border: none;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 2px 9px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
  }
}

.media-placeholder {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed var(--bs-primary);
  border-radius: var(--bs-border-radius);
  background-color: color-mix(in srgb, var(--bs-primary) 8%, transparent);
  color: var(--bs-primary);
  font-size: 0.78rem;
  font-weight: 500;

  &.drag-over {
    background-color: color-mix(in srgb, var(--bs-primary) 18%, transparent);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: color-mix(in srgb, var(--bs-primary) 18%, transparent);
    }
  }

  &.drag-over {
    border-style: solid;
    border-width: 2px;
  }
}

.add-media-btn {
  aspect-ratio: 1;
  border: 1px dashed var(--bs-secondary-color);
  border-radius: var(--bs-border-radius);
  background-color: transparent;
  color: var(--bs-secondary-color);
  display: flex;
  align-items: center;
  justify-content: center;

  &.drag-over {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: var(--bs-primary);
      color: var(--bs-primary);
    }
  }

  &.drag-over {
    border-style: solid;
    border-width: 2px;
    background-color: color-mix(in srgb, var(--bs-primary) 15%, transparent);
  }
}
</style>
